import { CircularProgress, LabelDisplayedRowsArgs, useMediaQuery } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { yellow } from '@material-ui/core/colors';
import Hidden from '@material-ui/core/Hidden';
import { Theme, useTheme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { DateTime } from 'luxon';
import { intersection, union, without } from 'ramda';
import React from 'react';
import { useIntl } from 'react-intl';
import {useLocation} from 'react-router';

import TablePaginationActions from '../../common/TablePaginationActions';
import config from '../../config';
import { useAxiosDispatch } from '../../middleware/axios';
import { createSnackbar } from '../../notifications/actions';
import { deleteCheckpoint, isDeleteCheckpointSuccess } from '../actions';
import { SelectedAction } from '../checkpointSelectionReducer';
import { messages } from '../list/CheckpointList.translations';
import { Checkpoint, Checkpoints } from '../state';

import { CheckpointActions } from './CheckpointActions';

const useStyles = makeStyles((theme: Theme) => ({
  highlight: {
    backgroundColor: yellow['200']
  },
  root: {
    backgroundColor: theme.palette.background.paper,
    maxWidth: 360,
    width: '100%'
  },
  spacer: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  tableCell: {
    whiteSpace: 'pre-wrap'
  },
  tableCellCentered: {
    textAlign: 'center'
  },
  tableRoot: {
    borderColor: theme.palette.divider,
    borderTop: 'solid 1px'
  },
  toolbar: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  }
}));

interface TableContentProps {
  recentlyModified: Checkpoints['recentlyModified'];
  pagedCheckpoints: string[];
}

interface EnhancedTableProps {
  selected: string[];
  dispatchSelected: (action: SelectedAction) => void;
}

interface Props extends EnhancedTableProps {
  checkpoints: Checkpoints;
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  loading: boolean;
}

export const CheckpointTable: React.FC<Props> = (props: Props) => {
  const {
    selected,
    dispatchSelected,
    checkpoints,
    page,
    handleChangePage,
    handleChangeRowsPerPage,
    rowsPerPage,
    loading
  } = props;

  const { formatMessage } = useIntl();
  const classes = useStyles();
  const theme = useTheme();
  const showRowsPerPage = useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useAxiosDispatch( );
  const allCodes = checkpoints.allCodes || [];

  const formatDate: (d: DateTime) => string = d => d.toLocaleString(DateTime.DATETIME_MED);
  const getLocationValue = (checkpoint: Checkpoint) => checkpoint.location ? checkpoint.location.name : 'Unassigned';
  const getTypeValues = (checkpoint: Checkpoint) =>
    checkpoint.types.length > 0 ?
      checkpoint.types.map(checkpointType => checkpointType.name).join(', ') : 'Unassigned';

  const isSelected = (code: string) => selected.indexOf(code) !== -1;

  const isEnabledCheckboxFeature = config.workManagement.showCheckboxFeature === 'true'
  || config.workManagement.showCheckboxFeature  === true;

  const handleSelectAllClick = (event: any) => {
    const inTable = union(allCodes, checkpoints.recentlyModified);
    // type assertion as we know that Object.values() won't return any undefined entries
    const allCheckpoints = Object.values(checkpoints.byCode) as Checkpoint[];
    const values = allCheckpoints.filter((cp) => inTable.includes(cp.code));
    const type = event.target.checked ? 'add' : 'remove';
    dispatchSelected({type, values});
  };

  const routeLocation = useLocation();
  const checkSequenceNumber = (code: string) => Boolean(checkpoints.byCode[code]?.sequenceNumber);

  const hasSequenceNumber = allCodes.some(checkSequenceNumber) ||
  checkpoints.recentlyModified.some(checkSequenceNumber);

  const CheckpointRow = ({ code, highlight }: { code: string, highlight: boolean }) => {
    const checkpoint = checkpoints.byCode[code];
    if (checkpoint == null) {
      throw new Error(`Checkpoint row code missing from state: "${code}"`);
    }

    const isItemSelected = isSelected(checkpoint.code);

    const handleClick = () => {
      dispatchSelected({ type: 'toggle', value: checkpoint });
    };

    function handleDeleteCheckpoint(): Promise<void> {
      return dispatch(deleteCheckpoint(code))
        .then((action) => {
          if (isDeleteCheckpointSuccess(action)) {
            dispatch(createSnackbar({
              message: formatMessage(messages['checkpoint.list.delete.success']),
              options: {
                variant: 'success'
              }
            }));
          } else {
            dispatch(createSnackbar({
              message: formatMessage(messages['checkpoint.list.delete.failure']),
              options: {
                variant: 'error'
              }
            }));
          }
        });
    }
    return (
      <TableRow
        key={code}
        className={highlight ? classes.highlight : ''}
        selected={isItemSelected}
      >
      {isEnabledCheckboxFeature ?
        (
          <Hidden mdDown={true}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={isItemSelected}
                onClick={handleClick}
                inputProps={{ 'aria-labelledby': code }}
              />
            </TableCell>
          </Hidden>
        )
        : null}
        {hasSequenceNumber ?
          (
            <Hidden mdDown={true}>
              <TableCell className={`${classes.tableCell}`}>
                {checkpoint.sequenceNumber}
              </TableCell>
            </Hidden>
          )
        : null}
        <TableCell className={classes.tableCell}>{checkpoint.name}</TableCell>
        <Hidden mdDown={true}>
        <TableCell className={classes.tableCell}>{checkpoint.description}</TableCell>
          <TableCell className={classes.tableCell}>{getLocationValue(checkpoint)}</TableCell>
          <TableCell className={classes.tableCell}>{getTypeValues(checkpoint)}</TableCell>
          <TableCell>{formatDate(checkpoint.created)}</TableCell>
          <TableCell>{formatDate(checkpoint.updated)}</TableCell>
        </Hidden>
        <TableCell>
          <CheckpointActions
            displayName={checkpoint.name}
            onDelete={handleDeleteCheckpoint}
            links={{
              delete: {pathname: `/checkpoints/${code}`, state: {previousLocation: routeLocation}},
              update: {pathname: `/checkpoints/${code}`, state: {previousLocation: routeLocation}}
            }}
            texts={{
              ariaLabel: formatMessage(messages['checkpoints.list.actions']),
              deleteText: formatMessage(messages['checkpoints.list.delete']),
              updateText: formatMessage(messages['checkpoints.list.update'])
            }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const recentlyModifiedOverlapWithAllCodes = allCodes.filter(code =>
    checkpoints.recentlyModified.indexOf(code) > -1);

  const pageCheckpoints = without(recentlyModifiedOverlapWithAllCodes, allCodes);

  // If a document that was recently modified appears on the page, don't count it twice
  const deDupedRecentlyModified = checkpoints.recentlyModified.length
    - intersection(allCodes, checkpoints.recentlyModified).length;

  const actualRowsPerPage = rowsPerPage + deDupedRecentlyModified;

  const displayedRows = ({ from, to, count }: LabelDisplayedRowsArgs) =>
    formatMessage(messages['checkpoints.list.displayedRows'], { from, to, count });

  const isSelectAllIndeterminate = !pageCheckpoints.every(x => selected.includes(x)) &&
    pageCheckpoints.some(x => selected.includes(x));

  const isSelectAllChecked = pageCheckpoints.every(x => selected.includes(x));

  const TableContents = ({ recentlyModified, pagedCheckpoints }: TableContentProps) => {
    if (recentlyModified.length === 0 && pagedCheckpoints.length === 0) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={8}>
            {formatMessage(messages['checkpoints.list.empty.primary'])}
          </TableCell>
        </TableRow>
      );
    }

    return (
      <>
        {recentlyModified.map(
          code => (<CheckpointRow key={code} code={code} highlight={true} />)
        )}
        {pagedCheckpoints.map(code => (
          <CheckpointRow key={code} code={code} highlight={false} />
        ))}
      </>
    );
  };
  return (
    <Table classes={{ root: classes.tableRoot }}>
      <TableHead>
        <TableRow>
          {isEnabledCheckboxFeature ?
            (
              <Hidden mdDown={true}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSelectAllIndeterminate}
                    checked={isSelectAllChecked}
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-label': formatMessage(messages['checkpoints.list.checkBox.label']) }}
                  />
                </TableCell>
              </Hidden>
            )
          : null}
          {hasSequenceNumber ?
            (
              <Hidden mdDown={true}>
                <TableCell>{formatMessage(messages['checkpoints.list.sequenceNumber'])}</TableCell>
              </Hidden>
            )
          : null}
          <TableCell>{formatMessage(messages['checkpoints.list.name'])}</TableCell>
          <Hidden mdDown={true}>
            <TableCell>{formatMessage(messages['checkpoints.list.description'])}</TableCell>
            <TableCell>{formatMessage(messages['checkpoints.list.location'])}</TableCell>
            <TableCell>{formatMessage(messages['checkpoints.list.types'])}</TableCell>
            <TableCell>{formatMessage(messages['checkpoints.list.created'])}</TableCell>
            <TableCell>{formatMessage(messages['checkpoints.list.updated'])}</TableCell>
          </Hidden>
          <TableCell>{formatMessage(messages['checkpoints.list.actions'])}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell align="center" colSpan={8}>
              <CircularProgress color="inherit" />
            </TableCell>
          </TableRow>
        ) : (
            <TableContents recentlyModified={checkpoints.recentlyModified} pagedCheckpoints={pageCheckpoints} />
          )}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            classes={{ spacer: classes.spacer, toolbar: classes.toolbar }}
            rowsPerPageOptions={showRowsPerPage ? [5, 10, 20] : []}
            count={checkpoints.count || 0}
            rowsPerPage={actualRowsPerPage}
            page={page}
            SelectProps={{
              inputProps: {
                'aria-label': formatMessage(messages['checkpoints.list.pagination.label'])
              },
              native: true
            }}
            onChangePage={(handleChangePage)}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            labelRowsPerPage={formatMessage(messages['checkpoints.list.rowsPerPage'])}
            labelDisplayedRows={displayedRows}
          />
        </TableRow>
      </TableFooter>
    </Table>
  );
};
